import PropTypes from 'prop-types';
import React from 'react';

import SeatType from './SeatType';
import styles from './map.scss';

const propTypes = {
  activeTicket: PropTypes.shape({}),
  width: PropTypes.number.isRequired,
  height: PropTypes.number.isRequired,
  scale: PropTypes.number.isRequired,
  canvasScale: PropTypes.number,
  rows: PropTypes.objectOf(PropTypes.shape({
    protected: PropTypes.number,
  })),
  seats: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string.isRequired,
    x: PropTypes.number.isRequired,
    y: PropTypes.number.isRequired,
    seat_type_id: PropTypes.string.isRequired,
    protected: PropTypes.number.isRequired,
  })),
  seatTypes: PropTypes.objectOf(PropTypes.shape({
    width: PropTypes.number.isRequired,
    height: PropTypes.number.isRequired,
    data: PropTypes.shape({
      color: PropTypes.string.isRequired,
      rotation: PropTypes.number,
      fill: PropTypes.string.isRequired,
    }).isRequired,
  })),
  onPressSeat: PropTypes.func.isRequired,
  validSeats: PropTypes.arrayOf(PropTypes.string),
  sections: PropTypes.objectOf(PropTypes.shape({
    protected: PropTypes.number,
  })),
  children: PropTypes.node,
};

class Seats extends React.Component {
  seatTypes = {};

  onPressCanvas = (evt) => {
    const { scale, onPressSeat, canvasScale } = this.props;
    if (typeof evt === 'undefined' && typeof evt.changedTouches === 'undefined' && evt?.changedTouches?.length === 0) { // Potentially fixes "undefined is not an object (evaluating 'r.clientX')"
      return;
    }
    // Note that this won't work if you have a seatmap that is scrolled out of view.
    // You would need to add scroll offset to the coordinates.
    const clientX = evt.clientX || evt.changedTouches[0].clientX;
    const clientY = evt.clientY || evt.changedTouches[0].clientY;
    const rect = this.canvas.getBoundingClientRect();
    const x = ((clientX - rect.left) / scale) * canvasScale;
    const y = ((clientY - rect.top) / scale) * canvasScale;

    const seat = this.findSeatByCoordinates(x, y);
    onPressSeat(seat);
  }

  findSeatByCoordinates = (x, y) => {
    if (!this.canvas) {
      return false;
    }

    const ctx = this.canvas.getContext('2d', { antialias: true });

    const { seats, seatTypes } = this.props;
    return seats && seats.find((seat) => {
      // const { ctx } = this.canvas;
      const type = seatTypes[seat.seat_type_id];
      const { width, height } = type;
      if (seat.rotation !== 0) {
        const deg = (Math.PI / 180) * seat.rotation;
        const pathPoints = [
          this.rotatedPoints(seat.x, seat.y, seat.x, seat.y, deg),
          this.rotatedPoints(seat.x + width, seat.y, seat.x, seat.y, deg),
          this.rotatedPoints(seat.x + width, seat.y + height, seat.x, seat.y, deg),
          this.rotatedPoints(seat.x, seat.y + height, seat.x, seat.y, deg),
        ];
        const path = new Path2D();
        path.moveTo(pathPoints[0].x, pathPoints[0].y);
        path.lineTo(pathPoints[1].x, pathPoints[1].y);
        path.lineTo(pathPoints[2].x, pathPoints[2].y);
        path.lineTo(pathPoints[3].x, pathPoints[3].y);

        if (ctx.isPointInPath(path, x, y)) {
          return true;
        }
      } else {
        return (x < seat.x + width) && (x > seat.x) && (y < seat.y + height) && (y > seat.y);
      }
      return false;
    });
  }

  rotatedPoints = (x, y, originX, originY, deg) => {
    const tempX = x - originX;
    const tempY = y - originY;

    const rotatedX = (tempX * Math.cos(deg)) - (tempY * Math.sin(deg));
    const rotatedY = (tempX * Math.sin(deg)) + (tempY * Math.cos(deg));

    return { x: rotatedX + originX, y: rotatedY + originY };
  }

  renderSeatmap = () => {
    /*

      TODO: GET ACTIVE TICKET TYPE AND SET UNAVAILABLE BASED ON THAT

    */
    if (!this.canvas) {
      return;
    }

    const {
      seats, seatTypes, validSeats, rows, sections, activeTicket, canvasScale,
    } = this.props;
    const ctx = this.canvas.getContext('2d', { antialias: true });

    if (!ctx) {
      return;
    }

    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    if (!seats) {
      return;
    }

    seats.forEach((seat) => {
      const {
        name,
        x: originalX,
        y: originalY,
        seat_type_id,
        section_id,
        row_id,
      } = seat;
      const {
        width: baseWidth,
        height: baseHeight,
        data,
      } = seatTypes[seat_type_id];

      // add rotations to each other, to get rotated rotated seats
      const rotation = (360 + seat.rotation + data.rotation) % 360;

      // Calculate the width/height of rotated shapes, needed to get the right
      // size source image, which lets us keep the correct aspect ratio and
      // placement of overlapping elements.
      const th = rotation * (Math.PI / 180);
      const width = (Math.abs(baseHeight * Math.sin(th)) + Math.abs(baseWidth * Math.cos(th)))
        / canvasScale;
      const height = (Math.abs(baseWidth * Math.sin(th)) + Math.abs(baseHeight * Math.cos(th)))
        / canvasScale;

      // Always calculate font size based on height as that is the measurement
      const fontHeight = (Math.min(baseHeight, baseWidth) * 0.7) / canvasScale;
      const font = `${fontHeight}px Roboto, sans-serif`;
      const locked = sections[section_id].protected || rows[row_id].protected || seat.protected;
      const unavailable = seat.claimed
        || (validSeats && !validSeats.includes(seat.id));
      const color = unavailable ? '#ffffff' : data && data.color;
      const availableModifier = (locked ? '.protected' : '.original');
      const modifier = unavailable ? '.unavailable' : availableModifier;
      const realModifier = (unavailable && locked && !activeTicket && !seat.claimed) ? '.protectedunavailable' : modifier;
      const src = this.seatTypes[`${seat_type_id}-${rotation}`].querySelector(realModifier);

      // The math behind positioning rotated shapes differs based on the rotation
      // due to where the axis of the rotation is (top left corner).
      let translateX = 0;
      let translateY = 0;
      if (rotation <= 90) { // 0-90
        translateX = (baseHeight * Math.sin(th));
        translateY = 0;
      } else if (rotation <= 180) { // 90 - 180
        translateX = (baseHeight * Math.sin(th)) - (baseWidth * Math.cos(th));
        translateY = -(baseHeight * Math.cos(th));
      } else if (rotation <= 270) { // 180 - 270
        translateX = -(baseWidth * Math.cos(th));
        translateY = -(baseWidth * Math.sin(th)) - (baseHeight * Math.cos(th));
      } else { // 270 - 360
        translateX = 0;
        translateY = -(baseWidth * Math.sin(th));
      }

      const x = (originalX - translateX) / canvasScale;
      const y = (originalY - translateY) / canvasScale;

      // Copy the right seat "background" and add seat name on top of it
      ctx.drawImage(src, x, y, width, height);
      // Don't draw text on top of the padlock
      if (realModifier !== '.protected' && realModifier !== '.protectedunavailable') {
        ctx.font = font;
        ctx.fillStyle = color;
        ctx.fillText(name, x + (width / 2), y + (height / 2) + (fontHeight / 8));
      }

      return null;
    });
  }

  render() {
    const {
      width,
      height,
      canvasScale,
      seatTypes,
      children,
      seats,
    } = this.props;

    // Create a new list of seat types including all kinds of rotations.
    // Some of them are marked as fake, those are not shown in the legend.
    /* eslint-disable no-param-reassign */
    const seatSeatTypes = seats && seats.reduce((ret, seat) => {
      // Does the types rotation + seat rotation exist?
      if (!ret[`${seat.seat_type_id}-${seat.rotation + seatTypes[seat.seat_type_id].data.rotation}`]) {
        const type = {
          ...seatTypes[seat.seat_type_id],
          data: { ...seatTypes[seat.seat_type_id].data },
          fake: seat.rotation !== 0,
        };

        type.data.rotation += seat.rotation;

        ret[`${seat.seat_type_id}-${type.data.rotation}`] = type;
      }

      // Make sure the original types are added as non-fake
      if (!ret[`${seat.seat_type_id}-${seatTypes[seat.seat_type_id].data.rotation}`]) {
        ret[`${seat.seat_type_id}-${seatTypes[seat.seat_type_id].data.rotation}`] = {
          ...seatTypes[seat.seat_type_id],
          fake: false,
        };
      }

      return ret;
    }, {});

    return (
      <>
        {seatTypes && Object.values(seatTypes) && (
        <div className={styles.legend}>
          { Object.values(seatSeatTypes)
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((seatType) => {
              const r = (360 + seatType.data.rotation) % 360;
              return (
                <SeatType
                  key={`${seatType.id}-${r}`}
                  seatType={seatType}
                  ref={(ref) => { this.seatTypes[`${seatType.id}-${r}`] = ref; }}
                />
              );
            })}
        </div>
        )}
        <div style={{
          position: 'relative',
          width: `${width / canvasScale}px`,
          height: `${height / canvasScale}px`,
          overflow: 'hidden',
        }}
        >
          <canvas
            className={styles.canvas}
            width={width / canvasScale}
            height={height / canvasScale}
            ref={(c) => {
              const firstRender = (this.canvas === undefined);
              this.canvas = c;
              // This triggers the first render of the seatmap
              // Its important to wait until the canvas ref is available before running it
              if (firstRender) {
                // Wait for the SeatType elements before we render
                window.requestAnimationFrame(this.renderSeatmap);
              }
            }}
            onClick={(evt) => this.onPressCanvas(evt)}
            onTouchEnd={(evt) => this.onPressCanvas(evt)}
          />
          {children}
        </div>

      </>
    );
  }
}

Seats.propTypes = propTypes;

export default Seats;
